import api from '../axiosConfig';
import { STAFF, DEPARTMENTS } from '../config/apiEndpoints';

export const StaffService = {
    /**
     * Fetches the list of all staff members.
     * x-tenant-id header is automatically handled by the axios interceptor.
     */
    getStaff: async (params = {}) => {
        try {
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(STAFF.BASE, { params: axiosParams });
            if (params.page !== undefined) return response.data;
            const data = response.data;
            return data.data || data || [];
        } catch (error) {
            console.error('StaffService.getStaff error:', error);
            throw error;
        }
    },

    getAttendance: async (params = {}) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(STAFF.ATTENDANCE, { 
                params: axiosParams,
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            if (params.page !== undefined) return response.data;
            const data = response.data;
            return data.data || data || [];
        } catch (error) {
            console.error('StaffService.getAttendance error:', error);
            throw error;
        }
    },

    recordAttendance: async (attendanceData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(STAFF.ATTENDANCE, attendanceData, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error('StaffService.recordAttendance error:', error);
            throw error;
        }
    },

    updateAttendance: async (id, updateData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.patch(`${STAFF.ATTENDANCE}/${id}`, updateData, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.updateAttendance (${id}) error:`, error);
            throw error;
        }
    },

    getLeaveRequests: async (params = {}) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(STAFF.LEAVES, { 
                params: axiosParams,
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            if (params.page !== undefined) return response.data;
            const data = response.data;
            return data.data || data || [];
        } catch (error) {
            console.error('StaffService.getLeaveRequests error:', error);
            throw error;
        }
    },

    requestLeave: async (leaveData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(STAFF.LEAVES, leaveData, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error('StaffService.requestLeave error:', error);
            throw error;
        }
    },

    approveLeaveRequest: async (id, payload) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.patch(STAFF.LEAVE_APPROVE(id), payload, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.approveLeaveRequest (${id}) error:`, error);
            throw error;
        }
    },

    getLeaveRequestById: async (id) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.get(STAFF.LEAVE_BY_ID(id), {
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            return data.data || data;
        } catch (error) {
            console.error(`StaffService.getLeaveRequestById (${id}) error:`, error);
            throw error;
        }
    },

    cancelLeaveRequest: async (id) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(STAFF.LEAVE_CANCEL(id), {}, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.cancelLeaveRequest (${id}) error:`, error);
            throw error;
        }
    },

    getStaffById: async (id) => {
        try {
            const response = await api.get(STAFF.BY_ID(id));
            const data = response.data;
            return data.data || data;
        } catch (error) {
            console.error(`StaffService.getStaffById (${id}) error:`, error);
            throw error;
        }
    },

    getStaffNotes: async (staffId, params = {}) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.get(`${STAFF.BY_ID(staffId)}/notes`, {
                params,
                headers: { 'x-tenant-id': tenantId }
            });
            const data = response.data;
            return data.data || data || [];
        } catch (error) {
            console.error(`StaffService.getStaffNotes (${staffId}) error:`, error);
            throw error;
        }
    },

    addStaffNote: async (staffId, noteData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(`${STAFF.BY_ID(staffId)}/notes`, noteData, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.addStaffNote (${staffId}) error:`, error);
            throw error;
        }
    },

    voidStaffNote: async (noteId) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(STAFF.VOID_NOTE(noteId), {}, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.voidStaffNote (${noteId}) error:`, error);
            throw error;
        }
    },

    addStaff: async (staffData) => {
        try {
            const response = await api.post(STAFF.BASE, staffData);
            return response.data;
        } catch (error) {
            console.error('StaffService.addStaff error:', error);
            throw error;
        }
    },

    updateStaff: async (id, updates) => {
        try {
            const response = await api.patch(STAFF.BY_ID(id), updates);
            return response.data;
        } catch (error) {
            console.error(`StaffService.updateStaff (${id}) error:`, error);
            throw error;
        }
    },

    deactivateStaff: async (id, deactivationData) => {
        try {
            const response = await api.post(STAFF.DEACTIVATE(id), deactivationData);
            return response.data;
        } catch (error) {
            console.error(`StaffService.deactivateStaff (${id}) error:`, error);
            throw error;
        }
    },

    getShifts: async (params = {}) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(STAFF.SHIFTS, { 
                params: axiosParams,
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            if (params.page !== undefined) return response.data;
            const data = response.data;
            const shiftList = Array.isArray(data) 
                ? data 
                : (Array.isArray(data?.data) 
                    ? data.data 
                    : (Array.isArray(data?.shifts) 
                        ? data.shifts 
                        : (Array.isArray(data?.data?.shifts) ? data.data.shifts : [])));
            return shiftList;
        } catch (error) {
            console.error('StaffService.getShifts error:', error);
            throw error;
        }
    },

    createShift: async (shiftData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(STAFF.SHIFTS, shiftData, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error('StaffService.createShift error:', error);
            throw error;
        }
    },

    updateShift: async (id, updates) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.patch(`${STAFF.SHIFTS}/${id}`, updates, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.updateShift (${id}) error:`, error);
            throw error;
        }
    },

    deleteShift: async (id) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.delete(`${STAFF.SHIFTS}/${id}`, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.deleteShift (${id}) error:`, error);
            throw error;
        }
    },

    getSchedule: async (filters = {}) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.get(STAFF.SCHEDULE, { 
                params: filters,
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            const data = response.data;
            return data.data || data || [];
        } catch (error) {
            console.error('StaffService.getSchedule error:', error);
            throw error;
        }
    },

    assignShift: async (assignmentData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(STAFF.ASSIGN_SHIFT, assignmentData, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error('StaffService.assignShift error:', error);
            throw error;
        }
    },

    updateAssignment: async (id, updates) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.patch(`${STAFF.SCHEDULE}/${id}`, updates, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.updateAssignment (${id}) error:`, error);
            throw error;
        }
    },

    deleteAssignment: async (id) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.delete(`${STAFF.SCHEDULE}/${id}`, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.deleteAssignment (${id}) error:`, error);
            throw error;
        }
    },

    getDepartments: async (params = {}) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const axiosParams = { ...params };
            if (params.page !== undefined) {
                axiosParams.page = params.page + 1;
            } else if (params.noPagination || params.noPaging) {
                axiosParams.noPagination = true;
            } else {
                axiosParams.noPagination = true;
            }
            const response = await api.get(DEPARTMENTS, {
                params: axiosParams,
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            if (params.page !== undefined) return response.data;
            const data = response.data;
            return data.data || data || [];
        } catch (error) {
            console.error('StaffService.getDepartments error:', error);
            throw error;
        }
    },

    createDepartment: async (departmentData) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(DEPARTMENTS, departmentData, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error('StaffService.createDepartment error:', error);
            throw error;
        }
    },

    updateDepartment: async (id, updates) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.patch(`${DEPARTMENTS}/${id}`, updates, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.updateDepartment (${id}) error:`, error);
            throw error;
        }
    },

    deleteDepartment: async (id) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.delete(`${DEPARTMENTS}/${id}`, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.deleteDepartment (${id}) error:`, error);
            throw error;
        }
    },

    updateDepartmentStatus: async (id, status) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.patch(`${DEPARTMENTS}/${id}/status`, { status }, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error(`StaffService.updateDepartmentStatus (${id}) error:`, error);
            throw error;
        }
    },

    reorderDepartments: async (departmentIds) => {
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const response = await api.post(`${DEPARTMENTS}/reorder`, { departmentIds }, {
                headers: {
                    'x-tenant-id': tenantId
                }
            });
            return response.data;
        } catch (error) {
            console.error('StaffService.reorderDepartments error:', error);
            throw error;
        }
    }
};
